import { Link, useLocation } from 'react-router-dom';

import { useTheme } from '../../hooks/useTheme';

import { isNavItemActive, type NavRailItem } from './config';
import { NAV_THEMES } from './nav-tokens';
import NavItemBadge from './NavItemBadge';
import { LABEL_CLASS, navRowClass, type NavItemVariant } from './navRowClass';

interface NavItemLinkProps {
  item: NavRailItem;
  variant: NavItemVariant;
  onNavigate?: () => void;
}

/** A drawn badge is part of what the link says: "Venues, 3 open tasks". */
function linkNameOf(item: NavRailItem, hasBadge: boolean): string {
  if (!hasBadge) return item.label;
  const count = item.badge ?? 0;
  return `${item.label}, ${item.badgeLabel ? item.badgeLabel(count) : count}`;
}

/** One navigable sub-item. `ready: false` items render visibly but inert —
 *  they name a bucket we have not built a route for yet. */
export default function NavItemLink({ item, variant, onNavigate }: NavItemLinkProps) {
  const { theme } = useTheme();
  const { pathname } = useLocation();
  const t = NAV_THEMES[theme];
  const Icon = item.icon;
  const isInert = item.ready === false;
  const label = isInert ? `${item.label} — coming soon` : item.label;
  const hasBadge = item.badge !== undefined && item.badge > 0;
  const linkName = linkNameOf(item, hasBadge);
  const rowClass = navRowClass(variant);
  const active = isNavItemActive(item, pathname);

  const content = (
    <>
      <Icon size={18} aria-hidden className="shrink-0" />
      <span className={LABEL_CLASS[variant]}>{item.label}</span>
      {item.tag && (
        <span
          className={`ml-auto shrink-0 rounded px-1 py-px text-[9px] font-bold tracking-wider ${LABEL_CLASS[variant]}`}
          style={{ background: t.cream, color: t.moss2 }}
        >
          {item.tag}
        </span>
      )}
      {hasBadge && <NavItemBadge count={item.badge ?? 0} variant={variant} />}
    </>
  );

  if (isInert) {
    return (
      <button
        type="button"
        disabled
        aria-label={label}
        title={label}
        className={`${rowClass} cursor-not-allowed`}
        style={{ color: t.faint }}
      >
        {content}
      </button>
    );
  }

  return (
    <Link
      to={item.to}
      onClick={onNavigate}
      aria-label={linkName}
      aria-current={active ? 'page' : undefined}
      title={linkName}
      className={`${rowClass} no-underline`}
      style={
        active
          ? { background: t.cream, color: t.barkDark }
          : { background: 'transparent', color: t.subtle }
      }
    >
      {content}
    </Link>
  );
}
